import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import {
  type ColumnFiltersState,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  type Updater,
  useReactTable,
} from '@tanstack/react-table';
import { CalendarDays, Plus, RotateCcw, SlidersHorizontal, Table } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCaseContext } from '@/components/contexts/CaseContext';
import { useMatterJurisdiction } from '@/components/contexts/MatterJurisdictionContext';
import { useShellHeader, useShellHeaderBelow } from '@/components/contexts/ShellHeaderContext';
import { useSidePanel } from '@/components/contexts/SidePanelContext';
import { DeadlinesCalendarView } from '@/components/deadlines/DeadlinesCalendarView';
import { DeadlinesDefaultFilters } from '@/components/deadlines/DeadlinesDefaultFilters';
import { DeadlinesFilterToolbar } from '@/components/deadlines/DeadlinesFilterToolbar';
import { PageLoading } from '@/components/shared/Loading';
import NoCaseSelected from '@/components/shared/NoCaseSelected';
import { BulkOutlookSyncDialog } from '@/components/tasks/components/BulkOutlookSyncDialog';
import { DeadlineDetailPanel } from '@/components/tasks/components/DeadlineDetailPanel';
import { useDeadlineHandlers } from '@/components/tasks/hooks/useDeadlineHandlers';
import { useDeadlineMutationsUi } from '@/components/tasks/hooks/useDeadlineMutationsUi';
import { useTaskDialogs } from '@/components/tasks/hooks/useTaskDialogs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { BulkEditDeadlinesDialog } from '@/components/ui/bulk-edit-deadlines-dialog';
import { type ActiveDeadlineFilters, type Task } from '@/components/ui/columns-tasks';
import { getDeadlineSortDate, getTableColumns } from '@/components/ui/columns-table';
import { CreateTaskDialog } from '@/components/ui/create-task-dialog';
import { DataTable } from '@/components/ui/data-table';
import { dateBoundDate, deriveFilterFields, type FilterField, type QuickFilterPreset } from '@/components/ui/data-table-filter-toolbar';
import { cn } from '@/lib/utils';
import { toLocalDate } from '@/utils/temporal';
import { getAssigneeOptions } from '@/components/ui/task-column-utils';
import { DeleteButton } from '@/components/ui/delete-button';
import { EditTaskDialog } from '@/components/ui/edit-task-dialog';
import { HeaderTabs } from '@/components/ui/HeaderTabs';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import { useCalendarSync } from '@/hooks/useCalendarSync';
import { useFolderTree } from '@/hooks/useFolders';
import useMatterMembers from '@/hooks/useMatterMembers';
import { remapTaskCalendars, useOutlookCalendars } from '@/hooks/useOutlookCalendars';
import { useTagVocabulary } from '@/hooks/useTagVocabulary';
import { useDeadlineActions } from '@/hooks/useDeadlineActions';
import { useDeadlines } from '@/hooks/useDeadlines';
import { findDeadlineByPublicId } from '@/lib/deadlines/find-by-public-id';
import {
  allSourceFolderGuids,
  deadlineMatchesSourceFolders,
  defaultSourceFolderGuids,
  folderTreeToSourceNodes,
} from '@/lib/deadlines/source-folders';

const DEADLINE_VIEW_TABS = [
  { value: 'table', label: 'Table', icon: Table },
  { value: 'calendar', label: 'Calendar', icon: CalendarDays },
] as const;

type DeadlineViewTab = (typeof DEADLINE_VIEW_TABS)[number]['value'];

// Default date window (in days) for the deadlines table: a "-30 days" past bound
// and a "+60 days" future bound, applied as two independent quick filters.
const DEFAULT_PAST_WINDOW_DAYS = 30;
const DEFAULT_FUTURE_WINDOW_DAYS = 60;

const DeadlinesPage = () => {
  const { t } = useTranslation('common');
  const { selectedCase } = useCaseContext();
  const { openDocumentPanel, activeDocumentGuid } = useSidePanel();
  const navigate = useNavigate();
  const { slug } = useParams({ from: '/matter/$slug/deadlines' });
  const { taskId: queryTaskId } = useSearch({ from: '/matter/$slug/deadlines' });
  const { region, forum } = useMatterJurisdiction();
  const [selectedTaskIdFromQuery, setSelectedTaskIdFromQuery] = useState<string | null>(queryTaskId ?? null);
  useShellHeader({
    header: t('deadlines'),
    subheader: 'Manage your deadlines',
  });

  // Tab + deleted-visibility state (declared before useDeadlines so the fetch can
  // opt into soft-deleted rows when the shared "Deleted" filter is Shown).
  const [activeTab, setActiveTab] = useState<DeadlineViewTab>('table');
  const [showDeleted, setShowDeleted] = useState(false);
  const [bulkOutlookDialogOpen, setBulkOutlookDialogOpen] = useState(false);
  // Row-detail side panel (Table): id of the deadline whose panel is open.
  const [detailTaskId, setDetailTaskId] = useState<string | null>(null);
  // Source click: the row that opened the document panel, cleared once that
  // document is no longer the one in the panel.
  const [openSource, setOpenSource] = useState<{ taskId: string; documentGuid: string } | null>(null);
  const includeDeleted = showDeleted;

  // Fetch deadlines and members from API
  const { deadlines: tasks, isLoading, isError, mutate } = useDeadlines(slug as string, { includeDeleted });
  const { members } = useMatterMembers(slug as string);
  const { folderTree, isLoading: foldersLoading, error: foldersError, mutate: mutateFolders } = useFolderTree(slug as string);
  // Toggling Deleted swaps the SWR key. Keep the last list for this matter so
  // that refetch leaves the toolbar, tabs, and current view in place.
  const deadlineRowsRef = useRef(tasks);
  const deadlineRowsSlugRef = useRef(slug);
  const deadlinesFetchedSlugRef = useRef<string | null>(null);
  if (deadlineRowsSlugRef.current !== slug) {
    deadlineRowsSlugRef.current = slug;
    deadlineRowsRef.current = [];
    deadlinesFetchedSlugRef.current = null;
  }
  if (!isLoading && !isError) {
    deadlineRowsRef.current = tasks;
    deadlinesFetchedSlugRef.current = slug;
  }
  const deadlineRows =
    isLoading && deadlinesFetchedSlugRef.current === slug ? deadlineRowsRef.current : tasks;
  const showPageLoading = (isLoading && deadlinesFetchedSlugRef.current !== slug) || foldersLoading;
  const { vocabulary: tagVocabulary } = useTagVocabulary(slug as string);
  const { calendars: availableCalendars, permissionGranted: outlookCalendarAvailable } = useOutlookCalendars();
  const { syncToOutlook, syncDeadlinesToOutlook, unsyncFromOutlook, isSyncing: isOutlookSyncing } =
    useCalendarSync(slug as string);

  // Dialog management hook
  const {
    createDialogOpen,
    editDialogOpen,
    selectedTask,
    openCreateDialog,
    openEditDialog,
    closeDialogs,
    setCreateDialogOpen,
    setEditDialogOpen,
  } = useTaskDialogs();

  // Close the create/edit dialogs after a successful create/update. Stable so
  // useDeadlineActions (and everything memoized off it) stays referentially stable.
  const handleActionSuccess = useCallback(() => {
    setCreateDialogOpen(false);
    setEditDialogOpen(false);
  }, [setCreateDialogOpen, setEditDialogOpen]);

  const taskActions = useDeadlineActions({
    slug: slug as string,
    mutate,
    onSuccess: handleActionSuccess,
  });

  useEffect(() => {
    setSelectedTaskIdFromQuery(queryTaskId ?? null);
  }, [queryTaskId]);

  // Deep link: ?taskId=... opens the Calendar tab and the deadline's detail panel.
  useEffect(() => {
    if (selectedTaskIdFromQuery) {
      setActiveTab('calendar');
      setDetailTaskId(selectedTaskIdFromQuery);
    }
  }, [selectedTaskIdFromQuery]);

  // Flat list: every non-deleted (or include-deleted) row is a deadline.
  // Never mutate the SWR-owned objects during render — build derived copies only
  // when the date needs parsing.
  const allTasks = useMemo(() => {
    return deadlineRows.reduce<Task[]>((acc, t) => {
      const date = toLocalDate(t.date);
      if (Number.isNaN(date.getTime())) return acc;
      acc.push(t.date instanceof Date ? t : { ...t, date });
      return acc;
    }, []);
  }, [deadlineRows]);

  const sourceFolders = useMemo(() => folderTreeToSourceNodes(folderTree), [folderTree]);
  const defaultGuids = useMemo(
    () => defaultSourceFolderGuids(sourceFolders, region, forum),
    [forum, region, sourceFolders],
  );
  const [selectedOverride, setSelectedOverride] = useState<Set<string> | null>(null);
  // Route reuse keeps this page mounted across matters — drop stale folder GUIDs.
  useEffect(() => {
    setSelectedOverride(null);
  }, [slug]);
  const allSourceGuids = useMemo(() => allSourceFolderGuids(sourceFolders), [sourceFolders]);
  // When no canonical defaults match, data is unrestricted; show the control as "all selected".
  const selectedSourceFolderGuids = useMemo(() => {
    if (selectedOverride !== null) return selectedOverride;
    if (defaultGuids.size > 0) return defaultGuids;
    return new Set(allSourceGuids);
  }, [allSourceGuids, defaultGuids, selectedOverride]);
  const allowedSourceFolderGuids = useMemo(() => {
    if (sourceFolders.length === 0) return null;
    // Empty selection is a restriction (hide extracted); do not treat it as "all".
    if (selectedSourceFolderGuids.size === allSourceGuids.length) return null;
    if (selectedOverride === null && defaultGuids.size === 0) return null;
    return selectedSourceFolderGuids;
  }, [
    allSourceGuids.length,
    defaultGuids.size,
    selectedOverride,
    selectedSourceFolderGuids,
    sourceFolders.length,
  ]);

  // Shared-calendar Graph IDs differ per user; resolve to this user's IDs so
  // chips and table Remove use the same records as the detail panel.
  const normalizedTasks = useMemo(
    () => remapTaskCalendars(allTasks, availableCalendars),
    [allTasks, availableCalendars],
  );

  const visibleDeadlines = useMemo(
    () =>
      normalizedTasks.filter((task) => deadlineMatchesSourceFolders(task, allowedSourceFolderGuids)),
    [allowedSourceFolderGuids, normalizedTasks],
  );
  const normalizedDeadlines = useMemo(
    () => normalizedTasks.filter((task) => !task.is_deleted),
    [normalizedTasks],
  );

  const currentSelectedTask = useMemo(() => {
    if (!selectedTask) return null;
    return allTasks.find((task) => task.id === selectedTask.id) ?? selectedTask;
  }, [allTasks, selectedTask]);

  const clearSelectedTaskQuery = useCallback(() => {
    if (!selectedTaskIdFromQuery) return;

    setSelectedTaskIdFromQuery(null);
    navigate({
      to: '/matter/$slug/deadlines',
      params: { slug },
      search: {},
      replace: true,
    });
  }, [navigate, selectedTaskIdFromQuery, slug]);

  // Delete / bulk-edit dialog state and their mutation handlers.
  const {
    deleteDialogOpen,
    taskToDelete,
    isDeleting,
    isBulkDeleting,
    bulkEditDialogOpen,
    setBulkEditDialogOpen,
    selectedDeadlinesForBulkEdit,
    setSelectedDeadlinesForBulkEdit,
    isBulkUpdating,
    clearBulkEditSelectionRef,
    handleDeleteTask,
    confirmDelete,
    handleCancelDelete,
    handleBulkDelete,
    handleRestoreDeadline,
    handleBulkRestore,
    handleBulkUpdate,
  } = useDeadlineMutationsUi({ taskActions, allTasks });

  const handleSourceRowOpen = useCallback((taskId: string, documentGuid: string) => {
    setOpenSource({ taskId, documentGuid });
  }, []);

  // Stable deadline-action handlers + the shared bundle for the Table columns and
  // the row detail panel.
  const {
    handleCreateTask,
    handleEditTask,
    handleSaveTags,
    deadlineHandlers,
  } = useDeadlineHandlers({
    taskActions,
    openCreateDialog,
    openEditDialog,
    openDocumentPanel,
    onDelete: handleDeleteTask,
    onRestore: includeDeleted ? handleRestoreDeadline : undefined,
    onSourceRowOpen: handleSourceRowOpen,
  });

  // Snapshot of active filters for column filterFns (search highlight, etc.).
  const activeFiltersRef = useRef<ActiveDeadlineFilters>({});

  // ============================================================================
  // COLUMN SETUP - Memoized to prevent unnecessary re-renders
  // ============================================================================

  // Litigation-theme tag labels, so the tags filter can treat old (legacy)
  // deadlines as litigation and surface them when any litigation tag is picked.
  const litigationTagLabels = useMemo(
    () => tagVocabulary.filter((item) => item.category === 'litigation').map((item) => item.label),
    [tagVocabulary],
  );

  // Stable memoized data to prevent unnecessary re-renders
  const memoizedDeadlines = useMemo(() => visibleDeadlines, [visibleDeadlines]);

  // Latest version of the deadline shown in the detail panel (kept in sync with
  // fetched data so edits/tag changes reflect live). Closes if the row vanishes.
  // A merged synthetic row's id is the synthetic id, but deep links (assignment
  // emails, notifications, PDF highlights) may carry event_guid or a collapsed
  // base Event.guid.
  const detailTask = useMemo(() => {
    if (!detailTaskId) return null;
    return findDeadlineByPublicId(normalizedTasks, detailTaskId) ?? null;
  }, [normalizedTasks, detailTaskId]);

  const handleSyncToCalendar = useCallback(
    async (taskGuid: string, calendarId: string, silent = false) => {
      const success = await syncToOutlook(taskGuid, calendarId, { silent });
      if (success) await mutate();
      return success;
    },
    [syncToOutlook, mutate],
  );

  const handleUnsyncFromCalendar = useCallback(
    async (taskGuid: string, calendarId: string, myCalendarId?: string, silent = false) => {
      const success = await unsyncFromOutlook(taskGuid, calendarId, myCalendarId, { silent });
      if (success) await mutate();
      return success;
    },
    [unsyncFromOutlook, mutate],
  );

  const handleBulkSyncToCalendar = useCallback(
    async (taskIds: string[], calendarId: string) => {
      const result = await syncDeadlinesToOutlook(taskIds, calendarId);
      if (result.synced > 0) await mutate();
      return result;
    },
    [syncDeadlinesToOutlook, mutate],
  );
  const handleOpenBulkOutlookDialog = useCallback(() => {
    setBulkOutlookDialogOpen(true);
  }, []);

  // Party options for the detail panel's inline party editor.
  const availableParties = useMemo(
    () =>
      Array.from(
        new Set(
          (deadlineRows || [])
            .map((t) => t.party)
            .filter((p): p is string => !!p && p !== 'Unknown'),
        ),
      ).sort(),
    [deadlineRows],
  );

  // The single column set that's actually rendered. The multi-field filter
  // toolbar config is derived from these same columns' `meta.filter` below, so
  // there's no second (filter-only) column factory to keep in sync.
  const tableColumns = useMemo(
    () =>
      getTableColumns(
        deadlineHandlers,
        deadlineRows,
        activeFiltersRef,
        slug as string,
        false,
        members,
        litigationTagLabels,
        tagVocabulary,
        {
          availableCalendars,
          calendarAvailable: outlookCalendarAvailable,
          onSyncToCalendar: (taskGuid, calendarId) =>
            syncToOutlook(taskGuid, calendarId, { silent: true }),
          onUnsyncFromCalendar: (taskGuid, calendarId, myCalendarId) =>
            unsyncFromOutlook(taskGuid, calendarId, myCalendarId, { silent: true }),
          onMenuClose: () => mutate(),
          isSyncing: isOutlookSyncing,
        },
      ),
    [
      deadlineHandlers,
      deadlineRows,
      activeFiltersRef,
      slug,
      members,
      litigationTagLabels,
      tagVocabulary,
      availableCalendars,
      outlookCalendarAvailable,
      syncToOutlook,
      unsyncFromOutlook,
      mutate,
      isOutlookSyncing,
    ],
  );

  // Config for the multi-field filter toolbar, derived from the rendered
  // columns' `meta.filter` (field order comes from the columns). Runtime option
  // lists that can't live statically on a column are supplied here as overrides.
  const filterFields = useMemo<FilterField[]>(() => {
    // Tag options come from the controlled vocabulary (litigation first, then the
    // Key Dates family), grouped into the two sub-sections shown in the filter.
    const tagOptions = tagVocabulary.map((item) => ({
      label: item.label,
      value: item.label,
      group: item.category === 'key_dates' ? 'Key Dates' : 'Litigation',
    }));
    const partyOptions = Array.from(new Set(allTasks.map((task) => task.party || 'Unknown')))
      .sort((a, b) => {
        if (a === 'Unknown') return 1;
        if (b === 'Unknown') return -1;
        return a.localeCompare(b);
      })
      .map((party) => ({ label: party, value: party }));
    return deriveFilterFields(tableColumns, {
      assignee: { options: members ? getAssigneeOptions(members) : [] },
      tags: { options: tagOptions },
      party: { options: partyOptions },
    });
  }, [tableColumns, members, tagVocabulary, allTasks]);

  // The Table adds a "Deleted" scope filter (externally controlled, server-side
  // fetch opts into soft-deleted rows). Defaults to hiding deleted deadlines.
  const tableFilterFields = useMemo<FilterField[]>(
    () => [
      ...filterFields,
      {
        id: 'deletedVisibility',
        label: 'Deleted',
        type: 'choice',
        options: [
          { label: 'Hidden', value: 'hide' },
          { label: 'Shown', value: 'show' },
        ],
        value: showDeleted ? 'show' : 'hide',
        defaultValue: 'hide',
        onChange: (value) => setShowDeleted(value === 'show'),
      },
    ],
    [filterFields, showDeleted],
  );

  // One-tap quick filters shown at the top of the table. The "-30 days" and
  // "+60 days" bounds combine into a single date window and toggle independently.
  const quickFilterPresets = useMemo<QuickFilterPreset[]>(
    () => [
      { label: `-${DEFAULT_PAST_WINDOW_DAYS} days`, columnId: 'date', dateBound: { side: 'past', days: DEFAULT_PAST_WINDOW_DAYS }, variant: 'button' },
      { label: `+${DEFAULT_FUTURE_WINDOW_DAYS} days`, columnId: 'date', dateBound: { side: 'future', days: DEFAULT_FUTURE_WINDOW_DAYS }, variant: 'button' },
      { label: 'Past', columnId: 'date', dateDirection: 'past', variant: 'button' },
      { label: 'Upcoming', columnId: 'date', dateDirection: 'future', variant: 'button' },
    ],
    [],
  );

  // Default the date window on first load, seeding each bound independently and
  // only when it catches at least one deadline: the "-30 days" bound applies when
  // there's a deadline in the last 30 days, the "+60 days" bound when there's one
  // in the next 60. If neither catches anything the table starts unfiltered.
  const initialColumnFilters = useMemo<ColumnFiltersState>(() => {
    const startToday = new Date();
    startToday.setHours(0, 0, 0, 0);
    const endToday = new Date();
    endToday.setHours(23, 59, 59, 999);
    const pastStart = dateBoundDate('past', DEFAULT_PAST_WINDOW_DAYS);
    const futureEnd = dateBoundDate('future', DEFAULT_FUTURE_WINDOW_DAYS);

    const hasItemsBetween = (start: number, end: number) =>
      visibleDeadlines.some((task) => {
        // Seed on the same canonical date the column sorts/filters on, so the
        // default window matches what a row actually filters against.
        const time = getDeadlineSortDate(task).getTime();
        return !Number.isNaN(time) && time >= start && time <= end;
      });

    const pastHasItems = hasItemsBetween(pastStart.getTime(), endToday.getTime());
    const futureHasItems = hasItemsBetween(startToday.getTime(), futureEnd.getTime());
    if (!pastHasItems && !futureHasItems) return [];

    return [
      {
        id: 'date',
        value: {
          startDate: pastHasItems ? pastStart : startToday,
          endDate: futureHasItems ? futureEnd : endToday,
        },
      },
    ];
  }, [visibleDeadlines]);

  // Shared by the table and calendar. Seeded once data is ready so the default
  // date window matches the table's previous first-mount behavior, then kept
  // across tab switches. A folder error leaves the tree empty, which drops the
  // source-folder restriction — don't seed from that set, or a later retry
  // keeps the wrong window.
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState | null>(null);
  const filtersCanSeed =
    typeof selectedCase !== 'undefined' &&
    !isLoading &&
    !foldersLoading &&
    !isError &&
    !foldersError;
  if (columnFilters === null && filtersCanSeed) {
    setColumnFilters(initialColumnFilters);
  }
  const activeColumnFilters = columnFilters ?? [];

  const handleColumnFiltersChange = useCallback((updater: Updater<ColumnFiltersState>) => {
    setColumnFilters((prev) => {
      const base = prev ?? [];
      return typeof updater === 'function' ? updater(base) : updater;
    });
  }, []);

  const handleClearAllFilters = useCallback(() => {
    setColumnFilters((prev) => {
      // The calendar doesn't show date filters, so clearing there leaves the
      // table's date window alone.
      if (activeTab === 'calendar') return (prev ?? []).filter((filter) => filter.id === 'date');
      return [];
    });
    setSelectedOverride(allSourceGuids.length > 0 ? new Set(allSourceGuids) : null);
  }, [activeTab, allSourceGuids]);

  // Same columns and filterFns as the table. The toolbar edits this state;
  // the calendar reads a copy that ignores the date filter, since month/week/day
  // navigation already chooses which dates are on screen.
  const filterTable = useReactTable({
    data: memoizedDeadlines,
    columns: tableColumns,
    state: { columnFilters: activeColumnFilters },
    onColumnFiltersChange: handleColumnFiltersChange,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    enableColumnFilters: true,
    getRowId: (row, index) => String(row.id ?? `row-${index}`),
    // Required by the app's SortingFns augmentation. This table only filters.
    sortingFns: {
      myCustomSorting: () => 0,
    },
  });
  const calendarColumnFilters = useMemo(
    () => activeColumnFilters.filter((filter) => filter.id !== 'date'),
    [activeColumnFilters],
  );
  const calendarFilterTable = useReactTable({
    data: memoizedDeadlines,
    columns: tableColumns,
    state: { columnFilters: calendarColumnFilters },
    // The updater is applied to the full shared list (including the hidden date
    // window). Facets below ignore that window because it is not in this state.
    onColumnFiltersChange: handleColumnFiltersChange,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    enableColumnFilters: true,
    getRowId: (row, index) => String(row.id ?? `row-${index}`),
    sortingFns: {
      myCustomSorting: () => 0,
    },
  });
  const filteredDeadlines = calendarFilterTable.getFilteredRowModel().rows.map((row) => row.original);
  const calendarFilterFields = useMemo(
    () => tableFilterFields.filter((field) => field.type !== 'date'),
    [tableFilterFields],
  );
  const toolbarFilterFields = activeTab === 'calendar' ? calendarFilterFields : tableFilterFields;
  const toolbarQuickFilters = activeTab === 'calendar' ? [] : quickFilterPresets;

  // Rendered in the shell header (below the title) via useShellHeaderBelow, not inline.
  // Memoized so the shell header doesn't re-dispatch on every unrelated re-render.
  const deadlinesHeaderBelow = useMemo(
    () => (
      <div className="flex items-end justify-between gap-4">
        <HeaderTabs
          items={DEADLINE_VIEW_TABS.map(({ value, label, icon }) => ({ value, label, icon }))}
          value={activeTab}
          onChange={(value) => setActiveTab(value as DeadlineViewTab)}
          idBase="deadlines-view"
        />
        <div className="flex items-center gap-2 pb-2">
          {outlookCalendarAvailable && availableCalendars.some((calendar) => calendar.canEdit) && (
            <Button
              onClick={handleOpenBulkOutlookDialog}
              variant="outline"
              size="sm"
              className="flex items-center gap-2"
            >
              <img src="/microsoft-outlook.svg" alt="" className="h-4 w-4 object-contain" />
              Sync to Outlook
            </Button>
          )}
          <Button
            onClick={() => setCreateDialogOpen(true)}
            variant="outline"
            size="sm"
            className="flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            {t('new-deadline')}
          </Button>
        </div>
      </div>
    ),
    [
      activeTab,
      availableCalendars,
      handleOpenBulkOutlookDialog,
      outlookCalendarAvailable,
      setCreateDialogOpen,
      t,
    ],
  );

  const renderSourceFolderDefaultsFilter = () => (
    <DeadlinesDefaultFilters
      folders={sourceFolders}
      selectedGuids={selectedSourceFolderGuids}
      onSelectedGuidsChange={setSelectedOverride}
      isRestricted={allowedSourceFolderGuids !== null}
      isDefaultSelection={selectedOverride === null}
      region={region}
      forum={forum}
    />
  );

  useShellHeaderBelow(typeof selectedCase === 'undefined' ? null : deadlinesHeaderBelow);

  const fillCalendarViewport =
    activeTab === 'calendar' && typeof selectedCase !== 'undefined' && !showPageLoading && !isError;

  return (
    <div className={cn(
      'overflow-x-hidden',
      (showPageLoading || fillCalendarViewport) && 'flex h-full min-h-0 flex-col',
      fillCalendarViewport && 'overflow-hidden',
    )}>
      <style>{`
        .overflow-x-hidden [class*="bg-"][class*="-100"]:not(:hover) {
          transition: none;
        }
        .overflow-x-hidden [class*="Badge"] {
          pointer-events: none;
        }
      `}</style>
      {typeof selectedCase === 'undefined' ? (
        <NoCaseSelected />
      ) : showPageLoading ? (
        <PageLoading />
      ) : isError || foldersError ? (
        <div className="flex flex-col items-center justify-center p-8 text-center">
          <p className="text-red-600 mb-4">
            {isError ? "Couldn't load deadlines. Try again." : "Couldn't load folders. Try again."}
          </p>
          <Button
            onClick={() => {
              if (isError) void mutate();
              if (foldersError) void mutateFolders();
            }}
            variant="outline"
          >
            Retry
          </Button>
        </div>
      ) : (
        <div
          className={cn(
            'relative z-20 px-6 pt-4',
            fillCalendarViewport ? 'flex h-full min-h-0 flex-col pb-4' : 'pb-6',
          )}
        >
          <div className={cn(fillCalendarViewport ? 'flex min-h-0 flex-1 flex-col' : 'mb-6')}>
            {/* Shared filter row so the table and calendar start at the same height. */}
            <div className="shrink-0 pt-4">
              <DeadlinesFilterToolbar
                table={activeTab === 'calendar' ? calendarFilterTable : filterTable}
                fields={toolbarFilterFields}
                quickFilterPresets={toolbarQuickFilters}
                toolbarExtra={renderSourceFolderDefaultsFilter()}
                toolbarExtraActiveCount={allowedSourceFolderGuids ? 1 : 0}
                onClearAllFilters={handleClearAllFilters}
              />
            </div>
            {/* Tab Content Wrapper */}
            <Tabs
              value={activeTab}
              onValueChange={(value) => setActiveTab(value as DeadlineViewTab)}
              className={cn('w-full', fillCalendarViewport && 'flex min-h-0 flex-1 flex-col')}
            >
              {/* Table View Content */}
              <TabsContent
                value="table"
                className="mt-0"
                id="deadlines-view-panel-table"
                aria-labelledby="deadlines-view-tab-table"
              >
              <DataTable
                  key="deadlines-table"
                  columns={tableColumns}
                  data={memoizedDeadlines}
                  hideSearch={true}
                  hideToolbar
                  activeFiltersRef={activeFiltersRef}
                  filterFields={tableFilterFields}
                  columnFilters={activeColumnFilters}
                  onColumnFiltersChange={handleColumnFiltersChange}
                  initialColumnVisibility={{ state: false, actions: false, tags: false, party: false }}
                  onRowActivate={(task) => setDetailTaskId((task as Task).id)}
                  highlightedRowIds={[
                    detailTaskId,
                    openSource && activeDocumentGuid === openSource.documentGuid ? openSource.taskId : null,
                  ]}
                  getRowClassName={(task) => {
                    const t = task as Task;
                    // The open row uses the shared blue highlight. Skip backgrounds
                    // that would cover it (`!` utilities beat data-state styles).
                    const sourceRowId =
                      openSource && activeDocumentGuid === openSource.documentGuid
                        ? openSource.taskId
                        : null;
                    const isOpenInPanel = t.id === detailTaskId || t.id === sourceRowId;
                    if (t.is_deleted) {
                      return isOpenInPanel
                        ? 'opacity-70'
                        : 'opacity-70 bg-red-50/40 hover:bg-red-50/60';
                    }
                    return undefined;
                  }}
                  hasPopupMenu={true}
                  headerAction={null}
                  onDelete={handleBulkDelete}
                  isDeleting={isBulkDeleting}
                  deleteConfirmationLabel="deadline"
                  popupMenuContent={({ selectedCount, selectedRows, onDelete, isDeleting: deleting, clearSelection }) => (
                    <div className="flex flex-row gap-2 justify-between items-center w-full">
                      <span className="text-sm text-gray-700">
                        {selectedCount} deadline{selectedCount !== 1 ? 's' : ''} selected
                      </span>
                      <div className="flex items-center gap-2">
                        {includeDeleted && (selectedRows as Task[]).some((r) => r.is_deleted) && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={async () => {
                              await handleBulkRestore((selectedRows as Task[]).map((r) => r.id));
                              clearSelection();
                            }}
                            disabled={deleting || isBulkUpdating}
                            className="flex items-center gap-1"
                          >
                            <RotateCcw className="h-4 w-4" />
                            Restore
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedDeadlinesForBulkEdit(selectedRows as Task[]);
                            clearBulkEditSelectionRef.current = clearSelection;
                            setBulkEditDialogOpen(true);
                          }}
                          disabled={deleting || isBulkUpdating}
                          className="flex items-center gap-1"
                        >
                          <SlidersHorizontal className="h-4 w-4" />
                          Edit
                        </Button>
                        <DeleteButton
                          onClick={onDelete}
                          isDeleting={deleting}
                        />
                      </div>
                    </div>
                  )}
                />
              </TabsContent>

              {/* Calendar View Content — fills the remaining pane so the last week stays visible */}
              <TabsContent
                value="calendar"
                className="mt-0 flex min-h-0 flex-1 flex-col overflow-hidden"
                id="deadlines-view-panel-calendar"
                aria-labelledby="deadlines-view-tab-calendar"
              >
                <DeadlinesCalendarView
                  tasks={filteredDeadlines}
                  onSelectTask={(taskId) => setDetailTaskId(taskId)}
                />
              </TabsContent>

            </Tabs>
          </div>

          {/* Dialogs */}
          <CreateTaskDialog
            isOpen={createDialogOpen}
            onClose={closeDialogs}
            onCreateTask={handleCreateTask}
            availableTasks={deadlineRows}
            members={members}
          />

          <EditTaskDialog
            isOpen={editDialogOpen}
            onClose={closeDialogs}
            onEditTask={handleEditTask}
            task={currentSelectedTask}
            members={members}
            availableTasks={deadlineRows}
            tagVocabulary={tagVocabulary}
            onSaveTags={handleSaveTags}
          />

          <DeadlineDetailPanel
            task={detailTask}
            open={!!detailTask}
            onOpenChange={(open) => {
              if (!open) {
                setDetailTaskId(null);
                clearSelectedTaskQuery();
              }
            }}
            slug={slug as string}
            members={members}
            availableParties={availableParties}
            tagVocabulary={tagVocabulary}
            handlers={deadlineHandlers}
            onEditTask={handleEditTask}
            availableCalendars={availableCalendars}
            outlookCalendarAvailable={outlookCalendarAvailable}
            onSyncToCalendar={handleSyncToCalendar}
            onUnsyncFromCalendar={handleUnsyncFromCalendar}
            isOutlookSyncing={isOutlookSyncing}
          />

          <BulkOutlookSyncDialog
            open={bulkOutlookDialogOpen}
            onOpenChange={setBulkOutlookDialogOpen}
            deadlines={normalizedDeadlines}
            availableCalendars={availableCalendars}
            onSync={handleBulkSyncToCalendar}
          />

          <BulkEditDeadlinesDialog
            open={bulkEditDialogOpen}
            onOpenChange={(open: boolean) => {
              setBulkEditDialogOpen(open);
              if (!open) {
                setSelectedDeadlinesForBulkEdit([]);
              }
            }}
            selectedDeadlines={selectedDeadlinesForBulkEdit}
            members={members}
            isApplying={isBulkUpdating}
            onApply={handleBulkUpdate}
          />

          {/* Delete Confirmation Dialog */}
          <AlertDialog open={deleteDialogOpen} onOpenChange={(open) => {
            if (!open) handleCancelDelete();
          }}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                <AlertDialogDescription>
                  {taskToDelete?.synthetic_deadline_id ? (
                    <>This will delete "{taskToDelete?.description || 'this deadline'}". You can restore it later from the Table's "Show deleted" view.</>
                  ) : (
                    <>This will permanently delete "{taskToDelete?.description || 'this deadline'}" from Crimson. This action cannot be undone.</>
                  )}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={confirmDelete}
                  disabled={isDeleting}
                  className="bg-red-600 hover:bg-red-700 focus:ring-red-600 text-white"
                >
                  {isDeleting ? 'Deleting...' : 'Delete'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

        </div>
      )}
    </div>
  );
};
export default DeadlinesPage;
